/**
 * **Document types** (`/settings/types`, W28, FR-65, plan D86; FR-5, FR-36) —
 * what a person may call a document when they upload or edit it, edited on
 * the page and saved by the bar (today’s `TypesView` and `TypesDialog`, the
 * dialog’s fields moved into place):
 *
 * - **Shipped with ProDoc** — each type’s label and its *in use* switch. A
 *   shipped type can be switched off, never removed; a document already of a
 *   type switched off keeps it. `other` is always in use.
 * - **The organisation’s own** — the codes it added, labelled and switched the
 *   same way, and the form that adds one: a code of lowercase letters, digits
 *   and `_`, not already in the list, and a label.
 *
 * A Save sends one `PATCH` with the version read and `document_types` alone.
 * A type without a label is said at its field and in the bar, and nothing is
 * sent until it has one. A reader sees every value, disabled, and the bar’s
 * *View only*.
 */
import { SETTINGS, type DocumentTypeSetting, type SettingsAnswer } from '@fiveox/prodoc-ui'
import { Button, Input, Switch, useToast } from '@fiveox/ui'
import { FilePlus2, FileType } from 'lucide-react'
import { useState } from 'react'

import { EditBar, EditPage, useLeaveGuard } from '../../components/edit'
import { SettingsSection } from '../../components/settings-parts'
import { useSettingsHost } from './host'
import { AreaWaiting, SETTINGS_BACK, useSettingsArea } from './settings-data'

const TYPE_CODE = /^[a-z][a-z0-9_]{1,39}$/

/** The page’s slice: every type as settings hold it, in order. */
const typesOf = (settings: SettingsAnswer): DocumentTypeSetting[] => settings.effective.document_types.map((t) => ({ ...t }))
/** The one key this page sends. */
const typesPatch = (types: DocumentTypeSetting[]) => ({ document_types: types.map((t) => ({ ...t, label: t.label.trim() })) })

/** Why a new code cannot be added, or `null` when it may. */
export function codeProblem(code: string, types: DocumentTypeSetting[]): string | null {
  if (!code) return null
  if (!TYPE_CODE.test(code)) return 'Lowercase letters, digits and _, starting with a letter (2–40).'
  if (types.some((t) => t.code === code)) return 'That code is already in the list.'
  return null
}

const UNLABELLED = 'A type needs a label — see the type marked above.'

export function TypesSettings() {
  const { can } = useSettingsHost()
  const { toast } = useToast()
  const area = useSettingsArea({ from: typesOf, toPatch: typesPatch })
  const guard = useLeaveGuard(area.dirty)
  const [code, setCode] = useState('')
  const [label, setLabel] = useState('')
  const editable = can(SETTINGS, 'can_update')
  const s = area.settings.data
  const types = area.draft

  // Said in the bar and at the field; Save waits until every type has a label.
  const unlabelled = Boolean(types?.some((t) => !t.label.trim()))
  const save = async () => {
    if (await area.save()) toast('Settings saved.')
  }
  const set = (which: string, next: Partial<DocumentTypeSetting>) => types && area.setDraft(types.map((t) => (t.code === which ? { ...t, ...next } : t)))

  // A settings answer without the shipped list counts every type as shipped, as the hub’s line does.
  const shippedCodes = new Set((s?.defaults.document_types ?? []).map((t) => t.code))
  const isShipped = (t: DocumentTypeSetting) => shippedCodes.size === 0 || shippedCodes.has(t.code)
  const codeError = types ? codeProblem(code, types) : null

  const row = (t: DocumentTypeSetting) => (
    <li key={t.code} className="grid gap-3 py-3 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_9rem] sm:items-center">
      <div className="min-w-0 space-y-1">
        <Input
          size="sm"
          aria-label={`Label for ${t.code}`}
          value={t.label}
          maxLength={60}
          disabled={!editable}
          onChange={(e) => set(t.code, { label: e.target.value })}
          error={t.label.trim() ? null : 'A type needs a label.'}
        />
        <p className="text-muted-foreground font-mono text-xs">{t.code}</p>
      </div>
      <Switch
        aria-label={`${t.label.trim() || t.code} in use`}
        label={t.code === 'other' ? 'Always in use' : t.enabled ? 'In use' : 'Off'}
        checked={t.enabled}
        disabled={!editable || t.code === 'other'}
        onChange={(e) => set(t.code, { enabled: e.target.checked })}
      />
    </li>
  )

  return (
    <EditPage title="Document types" description="What a person may call a document when they upload or edit it. Never guessed from the file." back={SETTINGS_BACK}>
      {guard}
      {!s || !types ? (
        <AreaWaiting settings={area.settings} label="Loading the document types" />
      ) : (
        <>
          <SettingsSection
            id="shipped"
            icon={<FileType size={18} aria-hidden />}
            title="Shipped with ProDoc"
            description="A type can be switched off, never removed; a document already of a type switched off keeps it. Other is always in use."
          >
            <ul className="divide-border divide-y" aria-label="Shipped types">
              {types.filter(isShipped).map(row)}
            </ul>
          </SettingsSection>

          <SettingsSection
            id="own"
            icon={<FilePlus2 size={18} aria-hidden />}
            title="The organisation’s own"
            description="Types the organisation added beside the shipped ones: a code that never changes, and the label people see."
          >
            <div className="space-y-5">
              {types.some((t) => !isShipped(t)) ? (
                <ul className="divide-border divide-y" aria-label="The organisation’s own types">
                  {types.filter((t) => !isShipped(t)).map(row)}
                </ul>
              ) : (
                <p className="text-muted-foreground text-sm">None yet — every type is one ProDoc ships.</p>
              )}
              <fieldset className="border-border space-y-2 border-t pt-4" disabled={!editable}>
                <legend className="text-foreground text-sm font-medium">Add a type of the organisation’s own</legend>
                <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-start">
                  <Input size="sm" label="Code" value={code} disabled={!editable} onChange={(e) => setCode(e.target.value.trim())} placeholder="lease_deed" error={codeError} />
                  <Input size="sm" label="Label" value={label} maxLength={60} disabled={!editable} onChange={(e) => setLabel(e.target.value)} placeholder="Lease deed" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="sm:mt-7"
                    disabled={!editable || !code || Boolean(codeError) || !label.trim()}
                    onClick={() => {
                      area.setDraft([...types, { code, label: label.trim(), enabled: true }])
                      setCode('')
                      setLabel('')
                    }}
                  >
                    Add
                  </Button>
                </div>
                <p className="text-muted-foreground text-xs">Added with the page’s Save, like any other change here.</p>
              </fieldset>
            </div>
          </SettingsSection>

          <EditBar
            dirty={area.dirty}
            saving={area.saving}
            problem={area.problem}
            invalid={unlabelled ? UNLABELLED : null}
            editable={editable}
            onSave={() => void save()}
            onDiscard={area.discard}
          />
        </>
      )}
    </EditPage>
  )
}
